/**
 * Everything the app knows about a file from its name alone.
 *
 * Before this module the extension→language table existed three times with
 * three different fallbacks, so the same `.yaml` file was highlighted in the
 * Workspace editor and plaintext in the chat panels. The table lives here once;
 * the disagreement that *was* intentional survives as the `fallback` argument.
 */

export const getFileExtension = (name) => name?.split('.').pop()?.toLowerCase() || '';

// Keys are lowercased extensions; values are Monaco language ids. Module scope
// so the object is allocated once — these components re-render on every
// keystroke in the editor.
export const MONACO_LANGUAGES = {
    // Markup. The old map had `md` only, so `.markdown` and `.mdx` opened as
    // plaintext even though the preview renders them as markdown.
    md: 'markdown',
    markdown: 'markdown',
    mdx: 'markdown',
    txt: 'plaintext',
    xml: 'xml',
    svg: 'xml',
    xsl: 'xml',
    xslt: 'xml',
    xsd: 'xml',
    plist: 'xml',
    config: 'xml',
    html: 'html',
    htm: 'html',

    // TypeScript / JavaScript
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    mjs: 'javascript',
    cjs: 'javascript',

    // Styles
    css: 'css',
    scss: 'scss',
    less: 'less',

    // Data
    json: 'json',
    jsonc: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    ini: 'ini',

    // Scripting
    py: 'python',
    sql: 'sql',
    sh: 'shell',
    bash: 'shell',
    zsh: 'shell',

    // Chat.js already accepts these on upload and classifies them as `code`,
    // but no map carried them, so they landed in the editor as plaintext.
    java: 'java',
    c: 'c',
    h: 'c',
    cpp: 'cpp',
    cc: 'cpp',
    cxx: 'cpp',
    hpp: 'cpp',
    go: 'go',
    rs: 'rust',
    rb: 'ruby',
    php: 'php',
    swift: 'swift',
    kt: 'kotlin',
    kts: 'kotlin',

    // An extension-less name falls out of split('.').pop() as the whole
    // filename, so `Dockerfile` resolves here without a second lookup table.
    dockerfile: 'dockerfile',
};

/**
 * @param {string} fileName
 * @param {string} [fallback] What an unrecognised extension becomes. The three
 *   call sites deliberately disagree: the Workspace editor is general-purpose
 *   (`plaintext`), the agent panel holds prose (`markdown`), and the mockup
 *   panel only ever holds JSX (`javascript`).
 */
export const getMonacoLanguage = (fileName, fallback = 'plaintext') =>
    MONACO_LANGUAGES[getFileExtension(fileName)] || fallback;
